<template>
  <v-card variant="text">
    <v-toolbar flat>
      <v-toolbar-title>{{ t('Variables') }}</v-toolbar-title>
      <v-spacer />
      <v-btn fab small color="primary" @click="newVariable" class="ma-2">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <ExportExcel :dataItems="variables" />
    </v-toolbar>

    <v-text-field
      class="mx-4 mt-4"
      v-model="search"
      :placeholder="t('Cerca')"
      prepend-inner-icon="mdi-magnify"
      single-line
      variant="outlined"
      hide-details
    />

    <v-data-table-virtual
      :headers="headers"
      :items="variables"
      :search="search"
      :loading="loading"
    >
      <template v-slot:[`item.action`]="{ item }">
        <v-icon small class="mr-2" @click="editVariable(item)">mdi-pencil</v-icon>
      </template>

      <template v-slot:loading>
        <v-skeleton-loader type="table-row@10" />
      </template>
    </v-data-table-virtual>
  </v-card>

  <v-dialog v-model="editDialog" persistent max-width="600px">
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          {{ editedVariable.id ? t('Editar') : t('Crear') }} {{ t('Variable') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="save" prepend-icon="mdi-content-save">
          {{ t('Guardar') }}
        </v-btn>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="editedVariable.nombre"
              :label="t('Nombre')"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="editedVariable.etiqueta"
              :label="t('Etiqueta')"
              required
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="editedVariable.tipo"
              :items="tipoOptions"
              :label="t('Tipo')"
              item-title="text"
              item-value="value"
              required
            />
          </v-col>
        </v-row>

        <v-row v-if="editedVariable.id">
          <v-col cols="12" class="pa-5">
            <v-btn
              block
              class="ma-3"
              color="red darken-1"
              text
              @click="openDeleteConfirmDialog()"
            >
              {{ t('Eliminar') }} {{ t('Variable') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            {{ t('Guardar_para_añadir_opciones') }}
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showDeleteConfirmDialog" persistent max-width="500px">
    <v-card>
      <v-card-title>{{ t('Estás_seguro') }}</v-card-title>
      <v-card-text>
        {{ t('¿Estás_seguro_que_quieres_eliminar') }} {{ t('la_variable') }} "{{ editedVariable.nombre }}"?
        {{ t('Esta_acción_es_irreversible') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="green darken-1" text @click="showDeleteConfirmDialog = false">
          {{ t('Cancelar') }}
        </v-btn>
        <v-btn color="red darken-1" text @click="confirmDeleteVariable">
          {{ t('Eliminar') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';
import ExportExcel from './ExportExcel.vue';
import { useUiStore } from '@/stores/ui';

const uiStore = useUiStore();
const { t } = useI18n();

// UI state
const search = ref('');
const variables = ref([]);
const loading = ref(false);
const editDialog = ref(false);
const showDeleteConfirmDialog = ref(false);
const editedVariable = ref({ id: null, nombre: '', etiqueta: '', tipo: 'texto' });

// Table headers
const headers = [
  { title: t('Nombre'), key: 'nombre' },
  { title: t('Etiqueta'), key: 'etiqueta' },
  { title: t('Tipo'), key: 'tipo' },
  { title: t('Acciones'), key: 'action', sortable: false },
];

// Tipo choices
const tipoOptions = [
  { text: t('Texto libre'), value: 'texto' },
  { text: t('Número (entero)'), value: 'numero' },
  { text: t('Número con decimales'), value: 'decimal' },
  { text: t('Sí/No'), value: 'booleano' },
  { text: t('Elección única'), value: 'eleccion' },
  { text: t('Elección múltiple'), value: 'multi' },
  { text: t('Escala Likert'), value: 'escala' },
  { text: t('Fecha'), value: 'fecha' },
];

// Fetch list
function fetchVariables() {
  loading.value = true;
  axios.get('variables/')
    .then(({ data }) => {
      variables.value = data;
    })
    .catch(err => console.error('Error fetching variables:', err))
    .finally(() => { loading.value = false; });
}

// Edit existing
function editVariable(item) {
  editedVariable.value = { ...item };
  editDialog.value = true;
}

// Create new
function newVariable() {
  editedVariable.value = { id: null, nombre: '', etiqueta: '', tipo: 'texto' };
  editDialog.value = true;
}

// Save create or update
function save() {
  const payload = {
    nombre: editedVariable.value.nombre,
    etiqueta: editedVariable.value.etiqueta,
    tipo: editedVariable.value.tipo,
  };
  const isUpdate = !!editedVariable.value.id;
  const method = isUpdate ? 'put' : 'post';
  const url = isUpdate ? `variables/${editedVariable.value.id}/` : 'variables/';

  axios[method](url, payload)
    .then(() => {
      fetchVariables();
      editDialog.value = false;
      uiStore.triggerSnackbar({
        message: `${editedVariable.value.nombre} ${t('fue_actualizado_con_exito')}`,
        color: 'success',
      });
    })
    .catch(error => {
      uiStore.triggerSnackbar({ message: error.message || error, color: 'error' });
      console.error('Error saving variable:', error);
    });
}

// Delete
function openDeleteConfirmDialog() { showDeleteConfirmDialog.value = true; }
function confirmDeleteVariable() {
  axios.delete(`variables/${editedVariable.value.id}/`)
    .then(() => {
      fetchVariables();
      editDialog.value = false;
      showDeleteConfirmDialog.value = false;
      uiStore.triggerSnackbar({ message: t('se_ha_eliminado_con_exito'), color: 'success' });
    })
    .catch(error => {
      uiStore.triggerSnackbar({ message: error.message || error, color: 'error' });
      console.error('Error deleting variable:', error);
    });
}

// Close dialog
function close() { editDialog.value = false; }

onMounted(fetchVariables);
</script>
